import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  IconButton,
  Link,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import {
  getRoster,
  getStudentActivity,
  getRemovedStudents,
  removeStudent,
  reinstateStudent,
  RosterStudent,
  RemovedStudent,
  StudentTopicActivity,
} from '../services/tutorRosterService';
import {
  getLifecycle,
  markStarted,
  markCompleted,
  TopicLifecycleDTO,
} from '../services/tutorLifecycleService';
import { isFlaggedForRevision, flagForRevision, unflagForRevision } from '../services/tutorRevisionService';
import StudentTopicActivityPanel from '../components/tutor/roster/StudentTopicActivityPanel';
import { getCourse, TutorCourse } from '../services/tutorCourseService';

export default function TutorCourseRosterPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const cId = Number(courseId);

  const [roster, setRoster] = useState<RosterStudent[]>([]);
  const [course, setCourse] = useState<TutorCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedStudentId, setExpandedStudentId] = useState<number | null>(null);
  const [activityCache, setActivityCache] = useState<Record<number, StudentTopicActivity[]>>({});
  const [activityLoading, setActivityLoading] = useState<number | null>(null);

  const [courseLifecycles, setCourseLifecycles] = useState<Map<number, TopicLifecycleDTO>>(new Map());
  const [courseRevisionFlags, setCourseRevisionFlags] = useState<Map<number, boolean>>(new Map());
  const [lifecycleLoaded, setLifecycleLoaded] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const [removedStudents, setRemovedStudents] = useState<RemovedStudent[]>([]);
  const [removedLoading, setRemovedLoading] = useState(false);
  const [removeActionLoading, setRemoveActionLoading] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadRoster = () => {
    Promise.all([getRoster(cId), getCourse(cId)])
      .then(([rosterData, courseData]) => {
        setRoster(rosterData);
        setCourse(courseData);
      })
      .catch(() => setError('Failed to load roster'))
      .finally(() => setLoading(false));
  };

  const loadRemovedStudents = () => {
    setRemovedLoading(true);
    getRemovedStudents(cId)
      .then(setRemovedStudents)
      .catch(() => {})
      .finally(() => setRemovedLoading(false));
  };

  useEffect(() => {
    loadRoster();
    loadRemovedStudents();
  }, [cId]);

  const handleRemoveStudent = async (studentId: number, displayName: string) => {
    if (!window.confirm(`Remove ${displayName} from this course? They will lose access but their data is preserved.`)) return;
    setRemoveActionLoading(studentId);
    try {
      await removeStudent(cId, studentId);
      setRoster(prev => prev.filter(s => s.studentId !== studentId));
      loadRemovedStudents();
      setSnackbar(`${displayName} has been removed from the course.`);
    } catch {
      setSnackbar('Failed to remove student. Please try again.');
    } finally {
      setRemoveActionLoading(null);
    }
  };

  const handleReinstateStudent = async (studentId: number, displayName: string) => {
    setRemoveActionLoading(studentId);
    try {
      await reinstateStudent(cId, studentId);
      loadRoster();
      loadRemovedStudents();
      setSnackbar(`${displayName} has been reinstated.`);
    } catch {
      setSnackbar('Failed to reinstate student. Please try again.');
    } finally {
      setRemoveActionLoading(null);
    }
  };

  const loadCourseLifecycles = useCallback(async (activities: StudentTopicActivity[]) => {
    if (lifecycleLoaded || activities.length === 0) return;
    const [lcResults, revResults] = await Promise.all([
      Promise.all(activities.map(a => getLifecycle(cId, a.nodeId).catch(() => null))),
      Promise.all(activities.map(a => isFlaggedForRevision(cId, a.nodeId).catch(() => false))),
    ]);
    const lcMap = new Map<number, TopicLifecycleDTO>();
    const revMap = new Map<number, boolean>();
    activities.forEach((a, i) => {
      if (lcResults[i]) lcMap.set(a.nodeId, lcResults[i]!);
      revMap.set(a.nodeId, revResults[i] as boolean);
    });
    setCourseLifecycles(lcMap);
    setCourseRevisionFlags(revMap);
    setLifecycleLoaded(true);
  }, [cId, lifecycleLoaded]);

  const handleToggleStudent = async (studentId: number) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null);
      return;
    }
    setExpandedStudentId(studentId);
    if (activityCache[studentId]) {
      await loadCourseLifecycles(activityCache[studentId]);
      return;
    }
    setActivityLoading(studentId);
    try {
      const activity = await getStudentActivity(cId, studentId);
      setActivityCache(prev => ({ ...prev, [studentId]: activity }));
      await loadCourseLifecycles(activity);
    } catch {
      setActivityCache(prev => ({ ...prev, [studentId]: [] }));
    } finally {
      setActivityLoading(null);
    }
  };

  const handleMarkStarted = async (nodeId: number) => {
    setActionLoading(nodeId);
    try {
      const lc = await markStarted(cId, nodeId);
      setCourseLifecycles(prev => new Map(prev).set(nodeId, lc));
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkCompleted = async (nodeId: number) => {
    setActionLoading(nodeId);
    try {
      const lc = await markCompleted(cId, nodeId);
      setCourseLifecycles(prev => new Map(prev).set(nodeId, lc));
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleRevision = async (nodeId: number, currentlyFlagged: boolean) => {
    setActionLoading(nodeId);
    try {
      if (currentlyFlagged) {
        await unflagForRevision(cId, nodeId);
        setCourseRevisionFlags(prev => new Map(prev).set(nodeId, false));
      } else {
        await flagForRevision(cId, nodeId);
        setCourseRevisionFlags(prev => new Map(prev).set(nodeId, true));
      }
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Link>
        {course && (
          <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tutor/courses/${cId}`)}>
            {course.name}
          </Link>
        )}
        <Typography color="text.primary">Student Roster</Typography>
      </Breadcrumbs>

      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {course ? `${course.name} — Student Roster` : 'Student Roster'}
      </Typography>

      {roster.length === 0 ? (
        <Alert severity="info">No students are enrolled in this course yet.</Alert>
      ) : (
        <Box>
          {roster.map((student) => {
            const isExpanded = expandedStudentId === student.studentId;
            const progressPct = student.totalTopics > 0
              ? Math.round((student.completedTopics / student.totalTopics) * 100)
              : 0;

            return (
              <Box key={student.studentId} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => handleToggleStudent(student.studentId)}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {student.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enrolled {new Date(student.enrolledAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${student.completedTopics}/${student.totalTopics} topics (${progressPct}%)`}
                    size="small"
                    color={progressPct === 100 ? 'success' : progressPct > 0 ? 'primary' : 'default'}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    title="Remove student from course"
                    onClick={(e) => { e.stopPropagation(); handleRemoveStudent(student.studentId, student.displayName); }}
                    disabled={removeActionLoading === student.studentId}
                  >
                    {removeActionLoading === student.studentId ? <CircularProgress size={16} /> : <PersonRemoveIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                  <Divider />
                  {activityLoading === student.studentId ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <StudentTopicActivityPanel
                      activities={activityCache[student.studentId] ?? []}
                      courseId={cId}
                      lifecycles={courseLifecycles}
                      revisionFlags={courseRevisionFlags}
                      onMarkStarted={handleMarkStarted}
                      onMarkCompleted={handleMarkCompleted}
                      onToggleRevision={handleToggleRevision}
                      actionLoading={actionLoading}
                    />
                  )}
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ─── Removed Students Section ─────────────────────────────────── */}
      <Box sx={{ mt: 4 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">
              Removed Students {removedStudents.length > 0 && `(${removedStudents.length})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {removedLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : removedStudents.length === 0 ? (
              <Box px={2} py={1.5}>
                <Typography variant="body2" color="text.secondary">No removed students.</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Enrolled</TableCell>
                    <TableCell>Removed</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {removedStudents.map((student) => (
                    <TableRow key={student.studentId}>
                      <TableCell>{student.displayName}</TableCell>
                      <TableCell>{new Date(student.enrolledAt).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(student.removedAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          disabled={removeActionLoading === student.studentId}
                          onClick={() => handleReinstateStudent(student.studentId, student.displayName)}
                        >
                          {removeActionLoading === student.studentId ? <CircularProgress size={14} /> : 'Reinstate'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Container>
  );
}
